import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Truck, Tag, Check, X } from "lucide-react";
import api, { formatBRL, formatApiErrorDetail } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

export default function Checkout() {
  const { items, total: subtotal } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: user?.name || "", address: "", cep: "" });
  const [loading, setLoading] = useState(false);

  const [shipping, setShipping] = useState(null); // {city, options}
  const [method, setMethod] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);

  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState(null); // {code, discount}
  const [couponLoading, setCouponLoading] = useState(false);

  if (items.length === 0) {
    navigate("/carrinho");
    return null;
  }

  const shippingPrice = shipping && method
    ? shipping.options.find((o) => o.method === method)?.price || 0
    : 0;
  const discount = applied?.discount || 0;
  const grandTotal = Math.max(0, subtotal - discount) + shippingPrice;

  const calcShipping = async () => {
    if (!form.cep) { toast.error("Informe seu CEP"); return; }
    setCalcLoading(true);
    try {
      const { data } = await api.post("/shipping/calc", { cep: form.cep, subtotal });
      setShipping(data);
      setMethod(data.options[0].method);
      toast.success(`Frete para ${data.city}`);
    } catch (e) {
      setShipping(null); setMethod(null);
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCalcLoading(false);
    }
  };

  const applyCoupon = async () => {
    if (!coupon) return;
    setCouponLoading(true);
    try {
      const { data } = await api.post("/coupons/validate", { code: coupon, subtotal });
      setApplied({ code: data.code, discount: data.discount });
      toast.success(`Cupom ${data.code} aplicado`);
    } catch (e) {
      setApplied(null);
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => { setApplied(null); setCoupon(""); };

  const handlePay = async () => {
    if (!form.name || !form.address) { toast.error("Preencha nome e endereço de entrega"); return; }
    if (!shipping || !method) { toast.error("Calcule e selecione o frete"); return; }
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, size: i.size, color: i.color, quantity: i.quantity })),
        origin_url: window.location.origin,
        shipping_name: form.name,
        shipping_address: form.address,
        cep: form.cep,
        shipping_method: method,
        coupon_code: applied?.code || null,
      };
      const { data } = await api.post("/checkout", payload);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-transparent border-b border-[#E6E5E1] focus:border-[#B89C65] outline-none py-3 text-[#1A1A1A] transition-colors";

  return (
    <div className="max-w-[1200px] mx-auto px-5 md:px-10 py-14">
      <h1 className="font-serif font-light text-5xl tracking-tight text-[#1A1A1A] mb-12">Finalizar compra</h1>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
        <div className="space-y-12">
          {/* delivery */}
          <div>
            <h2 className="text-[11px] uppercase tracking-[0.2em] text-[#B89C65] mb-6">Dados de entrega</h2>
            <div className="space-y-6">
              <div>
                <label className="text-[11px] uppercase tracking-[0.12em] text-[#5C5B59] block mb-1">Nome completo</label>
                <input data-testid="shipping-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.12em] text-[#5C5B59] block mb-1">Endereço</label>
                <textarea data-testid="shipping-address" rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                  placeholder="Rua, número, bairro, cidade" className={inputCls + " resize-none"} />
              </div>
            </div>
          </div>

          {/* shipping */}
          <div>
            <h2 className="text-[11px] uppercase tracking-[0.2em] text-[#B89C65] mb-6 flex items-center gap-2"><Truck size={15} /> Frete</h2>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className="text-[11px] uppercase tracking-[0.12em] text-[#5C5B59] block mb-1">CEP de entrega</label>
                <input data-testid="cep-input" value={form.cep} onChange={(e) => setForm({ ...form, cep: e.target.value })} placeholder="00000-000" className={inputCls} />
              </div>
              <button data-testid="calc-shipping-btn" onClick={calcShipping} disabled={calcLoading}
                className="border border-[#1A1A1A] text-[#1A1A1A] px-6 py-3 text-[11px] uppercase tracking-[0.15em] hover:bg-[#1A1A1A] hover:text-[#F8F7F4] transition-colors disabled:opacity-50">
                {calcLoading ? "…" : "Calcular"}
              </button>
            </div>
            {shipping && (
              <div className="mt-5 space-y-3" data-testid="shipping-options">
                {shipping.options.map((o) => (
                  <button key={o.method} data-testid={`ship-${o.method}`} onClick={() => setMethod(o.method)}
                    className={`w-full flex items-center justify-between border px-4 py-3.5 transition-colors ${method === o.method ? "border-[#1A1A1A]" : "border-[#E6E5E1] hover:border-[#1A1A1A]/40"}`}>
                    <span className="flex items-center gap-3">
                      <span className={`w-4 h-4 rounded-full border flex items-center justify-center ${method === o.method ? "border-[#1A1A1A]" : "border-[#C7C6C1]"}`}>
                        {method === o.method && <span className="w-2 h-2 rounded-full bg-[#1A1A1A]" />}
                      </span>
                      <span className="text-left">
                        <span className="block text-sm text-[#1A1A1A]">{o.label}</span>
                        <span className="block text-xs text-[#5C5B59]">até {o.days} dias úteis</span>
                      </span>
                    </span>
                    <span className="text-sm text-[#1A1A1A]">{o.free ? "Grátis" : formatBRL(o.price)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* coupon */}
          <div>
            <h2 className="text-[11px] uppercase tracking-[0.2em] text-[#B89C65] mb-6 flex items-center gap-2"><Tag size={15} /> Cupom de desconto</h2>
            {applied ? (
              <div data-testid="applied-coupon" className="flex items-center justify-between border border-[#1A1A1A] px-4 py-3.5">
                <span className="flex items-center gap-2 text-sm text-[#1A1A1A]"><Check size={16} className="text-[#B89C65]" /> {applied.code} — {formatBRL(applied.discount)}</span>
                <button data-testid="remove-coupon" onClick={removeCoupon} className="text-[#5C5B59] hover:text-[#1A1A1A]"><X size={16} /></button>
              </div>
            ) : (
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <input data-testid="coupon-input" value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="Ex.: BEMVINDA10" className={inputCls} />
                </div>
                <button data-testid="apply-coupon-btn" onClick={applyCoupon} disabled={couponLoading}
                  className="border border-[#1A1A1A] text-[#1A1A1A] px-6 py-3 text-[11px] uppercase tracking-[0.15em] hover:bg-[#1A1A1A] hover:text-[#F8F7F4] transition-colors disabled:opacity-50">
                  {couponLoading ? "…" : "Aplicar"}
                </button>
              </div>
            )}
            <p className="text-xs text-[#5C5B59] mt-2">Experimente: BEMVINDA10 (10% off) ou ATELIE50 (R$50 acima de R$300).</p>
          </div>
        </div>

        {/* summary */}
        <div>
          <div className="bg-[#FFFFFF] border border-[#E6E5E1] p-8 sticky top-32">
            <h2 className="font-serif text-2xl text-[#1A1A1A] mb-6">Seu pedido</h2>
            <div className="space-y-4 max-h-64 overflow-auto no-scrollbar">
              {items.map((i, idx) => (
                <div key={idx} className="flex gap-4">
                  <div className="w-14 bg-[#EFEEEA] overflow-hidden flex-shrink-0" style={{ height: "4.5rem" }}>
                    <img src={i.image} alt={i.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-[#1A1A1A] leading-tight">{i.name}</p>
                    <p className="text-[11px] uppercase tracking-[0.1em] text-[#5C5B59]">{[i.color, i.size].filter(Boolean).join(" · ")} · Qtd {i.quantity}</p>
                  </div>
                  <span className="text-sm text-[#1A1A1A]">{formatBRL(i.price * i.quantity)}</span>
                </div>
              ))}
            </div>
            <div className="h-px bg-[#E6E5E1] my-6" />
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-[#5C5B59]"><span>Subtotal</span><span data-testid="sum-subtotal">{formatBRL(subtotal)}</span></div>
              {discount > 0 && <div className="flex justify-between text-[#B89C65]"><span>Desconto ({applied.code})</span><span data-testid="sum-discount">- {formatBRL(discount)}</span></div>}
              <div className="flex justify-between text-[#5C5B59]"><span>Frete</span><span data-testid="sum-shipping">{shipping && method ? (shippingPrice === 0 ? "Grátis" : formatBRL(shippingPrice)) : "Calcule o CEP"}</span></div>
            </div>
            <div className="h-px bg-[#E6E5E1] my-4" />
            <div className="flex justify-between text-lg text-[#1A1A1A]"><span>Total</span><span data-testid="checkout-total">{formatBRL(grandTotal)}</span></div>

            <button data-testid="pay-btn" onClick={handlePay} disabled={loading}
              className="w-full flex items-center justify-center gap-3 bg-[#1A1A1A] text-[#F8F7F4] py-4 mt-6 text-[11px] uppercase tracking-[0.15em] hover:bg-[#B89C65] transition-colors disabled:opacity-60">
              <Lock size={15} /> {loading ? "Processando…" : "Pagar com Stripe"}
            </button>
            <p className="text-[11px] text-[#5C5B59] mt-4 text-center">Cartão teste: 4242 4242 4242 4242 · qualquer data · qualquer CVC</p>
          </div>
        </div>
      </div>
    </div>
  );
}
